import type { CaptionAnimationStyle, Subtitle, SubtitleWord } from "../types/timeline";
import type { TextClip, TextStyle } from "./types";
import { renderAnimatedCaption, type WordSegment } from "./caption-animation-renderer";

/**
 * Draws generated captions with per-word animation.
 *
 * Text clips are rendered by one shared 2D path (`titleEngine.renderText`) in
 * both preview and export, so putting caption painting here means karaoke that
 * looks right while scrubbing also burns into the exported file. There is no
 * second implementation to drift.
 */

export interface CaptionClipData {
  readonly words: readonly SubtitleWord[];
  readonly animationStyle: CaptionAnimationStyle;
  readonly highlightColor?: string;
  /** Wrap width as a fraction of frame width; defaults to 0.8. */
  readonly maxWidthRatio?: number;
}

type Ctx = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;

/**
 * Reads caption data off a text clip, or null when the clip is ordinary text.
 * Static captions return null too: nothing about them needs this path.
 */
export function getCaptionClipData(clip: TextClip): CaptionClipData | null {
  const meta = clip.metadata;
  if (!meta?.captionWords?.length) return null;

  const animationStyle = meta.captionAnimationStyle ?? "none";
  if (animationStyle === "none") return null;

  return {
    words: meta.captionWords,
    animationStyle,
    highlightColor: meta.captionHighlightColor,
    maxWidthRatio:
      typeof meta.captionMaxWidthRatio === "number"
        ? meta.captionMaxWidthRatio
        : undefined,
  };
}

interface LaidOutLine {
  readonly segments: WordSegment[];
  readonly width: number;
}

/**
 * The text-space width budget a caption wraps to.
 *
 * Deliberately independent of the clip's scale: scaling a caption zooms the
 * whole block uniformly, the way every caption editor behaves, so the line
 * breaks never move and the selection frame keeps matching what is painted.
 * The previous rule compensated by the inverse of the scale to pin the
 * on-screen column width instead, and a scaled-up caption degenerated into a
 * tower of single giant words while its box wrapped differently (QA
 * screenshot). The preview's selection geometry uses this same function.
 */
export function captionWrapWidth(
  frameWidth: number,
  maxWidthRatio: number | undefined,
): number {
  return frameWidth * (maxWidthRatio ?? 0.8);
}

/**
 * Greedy word wrap over a measured candidate line.
 *
 * Shared by the static caption branch and the preview's selection geometry so
 * the paint and the drag frame break lines in exactly the same places; two
 * implementations of "greedy" drift on the first edge case. A line with no
 * spaces comes back whole rather than sliced mid-word.
 */
export function wrapTextLine(
  line: string,
  maxWidth: number,
  measureWidth: (candidate: string) => number,
): string[] {
  const words = line.split(/\s+/).filter((w) => w.length > 0);
  if (words.length <= 1) return [line];
  const wrapped: string[] = [];
  let current = "";
  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word;
    if (current && measureWidth(candidate) > maxWidth) {
      wrapped.push(current);
      current = word;
    } else {
      current = candidate;
    }
  }
  if (current) wrapped.push(current);
  return wrapped;
}

/**
 * Breaks multi-word segments into per-word parts so the line wrap can work.
 *
 * Layout wraps BETWEEN segments, and a transcript that only carried
 * segment-level timings arrives as one segment holding the whole sentence,
 * which therefore could never wrap: a long caption ran straight off both
 * edges of the frame with its ends clipped, which QA reported with a
 * screenshot to prove it. Splitting such a segment into parts that inherit
 * its animation state changes nothing about timing or styling: the parts
 * highlight, fade, and move together exactly as the one segment did.
 *
 * A karaoke sweep is the one state a part cannot inherit verbatim, because
 * the gradient's stop describes progress across the WHOLE segment. The stop
 * is redistributed across the parts by character count: parts fully behind
 * the sweep take the filled gradient, parts ahead take the unfilled one, and
 * the boundary part keeps a proportional sweep of its own.
 */
export function splitSegmentsForLayout(
  segments: readonly WordSegment[],
): WordSegment[] {
  const out: WordSegment[] = [];
  for (const segment of segments) {
    const parts = segment.text.split(/\s+/).filter((p) => p.length > 0);
    if (parts.length <= 1) {
      out.push(segment);
      continue;
    }

    const stopMatch =
      segment.color && segment.color.startsWith("linear-gradient")
        ? /(\d+(?:\.\d+)?)%/.exec(segment.color)
        : null;
    const sweep = stopMatch ? Number(stopMatch[1]) / 100 : null;
    const totalChars = parts.reduce((n, p) => n + p.length, 0);

    let consumed = 0;
    for (const part of parts) {
      let color = segment.color;
      if (sweep !== null && segment.color) {
        const startFraction = consumed / totalChars;
        const endFraction = (consumed + part.length) / totalChars;
        const local =
          endFraction <= sweep
            ? 100
            : startFraction >= sweep
              ? 0
              : ((sweep - startFraction) / (endFraction - startFraction)) * 100;
        color = segment.color.replace(
          /\d+(?:\.\d+)?%/g,
          `${local.toFixed(1)}%`,
        );
      }
      // The active-word pop exists to make one word stand out from its
      // neighbours. Split parts are all the active word at once, and popping
      // each about its own centre inflates every word into the gap beside it,
      // which reads as the spaces going missing. The shared offsetY stays: a
      // uniform bounce moves the parts together, exactly as the one segment
      // moved.
      out.push({
        ...segment,
        text: part,
        scale: 1,
        ...(color !== undefined ? { color } : {}),
      });
      consumed += part.length;
    }
  }
  return out;
}

/**
 * Greedy word wrap. Measures with the caption's own font so the result matches
 * what actually gets painted.
 */
function layoutLines(
  ctx: Ctx,
  segments: WordSegment[],
  maxWidth: number,
): LaidOutLine[] {
  const spaceWidth = ctx.measureText(" ").width;
  const lines: LaidOutLine[] = [];
  let current: WordSegment[] = [];
  let width = 0;

  for (const segment of segments) {
    const segmentWidth = ctx.measureText(segment.text).width;
    const added = current.length === 0 ? segmentWidth : spaceWidth + segmentWidth;

    if (current.length > 0 && width + added > maxWidth) {
      lines.push({ segments: current, width });
      current = [segment];
      width = segmentWidth;
      continue;
    }

    current.push(segment);
    width += added;
  }

  if (current.length > 0) {
    lines.push({ segments: current, width });
  }

  return lines;
}

/**
 * The karaoke animation describes a half-filled word as a CSS
 * `linear-gradient(...)` string. Canvas silently ignores an unparseable
 * `fillStyle`, which would leave the word being spoken painted in the base
 * colour, the one word that must stand out. Rebuild it as a real canvas
 * gradient with a hard stop at the sweep position.
 */
export function resolveFill(
  ctx: Ctx,
  requested: string,
  segmentWidth: number,
  highlightColor: string,
  baseColor: string,
): string | CanvasGradient {
  if (!requested.startsWith("linear-gradient")) return requested;

  const stop = /(\d+(?:\.\d+)?)%/.exec(requested);
  const progress = stop ? Math.min(100, Math.max(0, Number(stop[1]))) / 100 : 0;

  try {
    const gradient = ctx.createLinearGradient(
      -segmentWidth / 2,
      0,
      segmentWidth / 2,
      0,
    );
    gradient.addColorStop(0, highlightColor);
    gradient.addColorStop(progress, highlightColor);
    gradient.addColorStop(Math.min(1, progress + 0.001), baseColor);
    gradient.addColorStop(1, baseColor);
    return gradient;
  } catch {
    // A zero-width segment makes createLinearGradient throw; the flat
    // highlight is a fine stand-in for a word too small to sweep.
    return highlightColor;
  }
}

/** Draws the caption's plate behind a line of text. */
function paintBackground(
  ctx: Ctx,
  style: TextStyle,
  lineWidth: number,
  y: number,
  lineHeight: number,
): void {
  if (!style.backgroundColor) return;

  const padX = style.fontSize * 0.3;
  const padY = style.fontSize * 0.12;

  ctx.save();
  ctx.fillStyle = style.backgroundColor;
  ctx.fillRect(
    -lineWidth / 2 - padX,
    y - lineHeight / 2 - padY,
    lineWidth + padX * 2,
    lineHeight + padY * 2,
  );
  ctx.restore();
}

function applyStyle(ctx: Ctx, style: TextStyle): void {
  ctx.font = `${style.fontStyle} ${style.fontWeight} ${style.fontSize}px "${style.fontFamily}", sans-serif`;
  ctx.textBaseline = "middle";
  ctx.textAlign = "left";
}

/**
 * Paints one animated caption frame.
 *
 * Returns false when the caption is not on screen at this time, so the caller
 * can skip the rest of its own drawing.
 */
export function renderCaptionFrame(
  ctx: Ctx,
  data: CaptionClipData,
  text: string,
  style: TextStyle,
  clipDuration: number,
  localTime: number,
  maxWidth: number,
): boolean {
  // renderAnimatedCaption works in absolute subtitle time; our word timings are
  // clip-relative, so the synthetic subtitle simply starts at zero.
  //
  // The highlight colour is handed over through `style.highlightColor` (the
  // channel the animation renderer already reads) rather than being patched
  // onto the segments afterwards, which would fight its own default.
  const subtitle: Subtitle = {
    id: "caption-clip",
    text,
    startTime: 0,
    endTime: clipDuration,
    words: data.words as SubtitleWord[],
    animationStyle: data.animationStyle,
    style: {
      fontFamily: style.fontFamily,
      fontSize: style.fontSize,
      color: style.color,
      backgroundColor: style.backgroundColor ?? "transparent",
      position: "bottom",
      highlightColor: data.highlightColor ?? style.color,
    },
  };

  const frame = renderAnimatedCaption(subtitle, localTime);
  if (!frame.visible || frame.segments.length === 0) return false;

  applyStyle(ctx, style);

  const lines = layoutLines(ctx, splitSegmentsForLayout(frame.segments), maxWidth);
  const lineHeight = style.fontSize * style.lineHeight;
  const totalHeight = lines.length * lineHeight;
  const spaceWidth = ctx.measureText(" ").width;

  let y = -totalHeight / 2 + lineHeight / 2;

  for (const line of lines) {
    // Painted per line, before the words, so a preset built around a plate
    // ("Soft", "Typewriter") keeps its plate instead of losing contrast.
    paintBackground(ctx, style, line.width, y, lineHeight);

    let x =
      style.textAlign === "left"
        ? -maxWidth / 2
        : style.textAlign === "right"
          ? maxWidth / 2 - line.width
          : -line.width / 2;

    for (const segment of line.segments) {
      const segmentWidth = ctx.measureText(segment.text).width;

      if (segment.style !== "hidden" && segment.opacity > 0) {
        ctx.save();

        // Scale each word about its own centre so a bouncing word grows in
        // place instead of sliding right.
        const centerX = x + segmentWidth / 2;
        const centerY = y + segment.offsetY;
        ctx.translate(centerX, centerY);
        ctx.scale(segment.scale, segment.scale);
        ctx.globalAlpha *= segment.opacity;

        const requested =
          segment.color ??
          (segment.style === "active" || segment.style === "highlighted"
            ? (data.highlightColor ?? style.color)
            : style.color);

        const fill = resolveFill(
          ctx,
          requested,
          segmentWidth,
          data.highlightColor ?? style.color,
          style.color,
        );

        if (style.shadowColor && style.shadowBlur) {
          ctx.shadowColor = style.shadowColor;
          ctx.shadowBlur = style.shadowBlur;
          ctx.shadowOffsetX = style.shadowOffsetX ?? 0;
          ctx.shadowOffsetY = style.shadowOffsetY ?? 0;
        }

        if (style.strokeColor && style.strokeWidth) {
          ctx.lineWidth = style.strokeWidth;
          ctx.strokeStyle = style.strokeColor;
          ctx.lineJoin = "round";
          ctx.miterLimit = 2;
          ctx.strokeText(segment.text, -segmentWidth / 2, 0);
        }

        ctx.fillStyle = fill;
        ctx.fillText(segment.text, -segmentWidth / 2, 0);

        ctx.restore();
      }

      x += segmentWidth + spaceWidth;
    }

    y += lineHeight;
  }

  return true;
}
